/* -------------------------------------------------------------
 * Customized Gallery App Integration Logic
 * Designed for Altenew Crafters' Gallery
 * Extremely Optimized, High-Performance Refactored Version
 * ------------------------------------------------------------- */

// ── Global Scope Utilities and One-time Bindings ───────────────────

// Force scroll position to the top of the page on reload/refresh to prevent native scroll clamping
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}
window.scrollTo(0, 0);

// Global Interceptor: Prevent any click bubble from performing default navigation to external share URLs
document.addEventListener('click', function(e) {
  const anchor = e.target.closest('a');
  if (anchor) {
    const href = anchor.getAttribute('href') || '';
    if (href.includes('/share/') || href.includes('open-gallery-item=') || href.includes('gallery-item=')) {
      // If it's a click originating from our premium custom grid triggers, prevent default navigation
      if (e.target.closest('.custom-gallery-grid-container') || e.isTrusted === false) {
        e.preventDefault();
      }
    }
  }
}, true); // Capturing phase to catch it before standard browser action

// Bulletproof copy to clipboard utility (HTTP/HTTPS compliant)
function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  } else {
    return new Promise((resolve, reject) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        resolve();
      } catch (err) {
        reject(err);
      } finally {
        document.body.removeChild(textarea);
      }
    });
  }
}

// Dynamic helper to find the original app wrapper anywhere on the page
function getOriginalWrapper() {
  const activeSection = document.querySelector('.custom-gallery-main-section');
  if (!activeSection) return null;
  return activeSection.querySelector('.original-app-gallery-wrapper') ||
      document.querySelector(
          '.switch-apps-customer-gallery, [data-uploadify-gallery], .uploadify-gallery');
}

// Robustly locate the active native image element corresponding to the custom card
function findLiveOriginalImage(imgSrc) {
  const originalWrapper = getOriginalWrapper();
  if (!originalWrapper) return null;

  const imgs = originalWrapper.querySelectorAll('img');
  
  // 1. Direct absolute property match (extremely precise as browser resolves relative URLs to absolute)
  for (const img of imgs) {
    if (img.src === imgSrc) {
      return img;
    }
  }

  // 2. Fallback to matching image filename (ignoring protocol, query params and CDN sizing tags)
  try {
    const filename = imgSrc.split('/').pop().split('?')[0].replace('uploadify--', '');
    if (filename) {
      for (const img of imgs) {
        const curSrc = img.src || img.getAttribute('src') || '';
        if (curSrc.includes(filename)) {
          return img;
        }
      }
    }
  } catch (e) {
    console.error('[Gallery Finder] Error matching image by filename:', e);
  }

  // 3. Fallback exact selector
  try {
    const fallbackSel = originalWrapper.querySelector(`img[src="${imgSrc}"]`);
    if (fallbackSel) return fallbackSel;
  } catch (e) {}

  return null;
}

// High-performance background sharing redirect using native URL hash tracking
function triggerNativeSocialClick(cardData, platform) {
  const liveImage = findLiveOriginalImage(cardData.imgSrc);
  if (!liveImage) {
    console.warn('[Gallery Trigger] Live original image not found to trigger lightbox.');
    return;
  }

  document.body.classList.add('cg-is-loading');
  
  // Natively, clicking the image will open the lightbox and update the URL hash
  liveImage.click();

  let checkCount = 0;
  const maxChecks = 150; // Check up to 1.5 seconds

  const timer = setInterval(() => {
    checkCount++;
    
    const currentHash = window.location.hash || '';
    const match = currentHash.match(/open-gallery-item=([\w-]+)/) || 
                  currentHash.match(/gallery-item=([\w-]+)/);

    if (match && match[1]) {
      clearInterval(timer);
      const extractedId = match[1];

      // 1. Immediately close the native lightbox modal cleanly
      if (typeof window.$ === 'function') {
        window.$('.switch-apps-popup-close').click();
      } else if (typeof window.jQuery === 'function') {
        window.jQuery('.switch-apps-popup-close').click();
      } else {
        const closeBtns = document.querySelectorAll('.switch-apps-popup-close');
        closeBtns.forEach(btn => btn.click());
      }

      // Safe cleanup of hash to prevent page jumping or leaving the modal active in state
      setTimeout(() => {
        if (window.location.hash.includes('open-gallery-item=')) {
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, null, window.location.pathname + window.location.search);
          } else {
            window.location.hash = '';
          }
        }
        document.body.classList.remove('cg-is-loading');
      }, 100);

      // 2. Construct the exact premium Deep-Link and Share URL
      const deepLinkUrl = `${window.location.origin}${window.location.pathname}#open-gallery-item=${extractedId}`;
      const base64DeepLink = window.btoa(deepLinkUrl);
      const originalShareUrl = `${window.location.origin}/apps/uploadify-customer-gallery/share/${extractedId}?url=${base64DeepLink}`;

      // 3. Open share popups or trigger copy-to-clipboard actions
      const popupWidth = 650;
      const popupHeight = 450;
      const screenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
      const screenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
      
      const outerWidth = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
      const outerHeight = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;
      
      const left = screenLeft + (outerWidth - popupWidth) / 2;
      const top = screenTop + (outerHeight - popupHeight) / 2;
      const popupFeatures = `width=${popupWidth},height=${popupHeight},top=${top},left=${left},status=no,resizable=yes,scrollbars=yes`;

      if (platform === 'facebook') {
        const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(originalShareUrl)}`;
        window.open(shareUrl, '_blank', popupFeatures);
      } else if (platform === 'twitter') {
        const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(originalShareUrl)}&text=${encodeURIComponent(cardData.title || 'Check out this craft creation!')}`;
        window.open(shareUrl, '_blank', popupFeatures);
      } else if (platform === 'instagram') {
        copyToClipboard(originalShareUrl).then(() => {
          showToast('Link Copied! Share it on Instagram.');
        });
      } else if (platform === 'copy') {
        copyToClipboard(originalShareUrl).then(() => {
          showToast('Link Copied!');
        });
      }
      return;
    }

    if (checkCount >= maxChecks) {
      clearInterval(timer);
      document.body.classList.remove('cg-is-loading');
      console.warn('[Gallery Trigger] Timeout waiting for hash change to extract database ID.');
      
      // Safe fallback for copy link
      if (platform === 'copy' || platform === 'instagram') {
        const fallbackUrl = `${window.location.origin}${window.location.pathname}#open-gallery-item=${cardData.postId}`;
        copyToClipboard(fallbackUrl).then(() => {
          showToast('Link Copied!');
        });
      }
    }
  }, 10);
}

// Toast Notification Utility
function showToast(message) {
  let toast = document.querySelector('.cg-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'cg-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('cg-toast--visible');
  clearTimeout(toast._hideTimer);
  toast._hideTimer = setTimeout(() => toast.classList.remove('cg-toast--visible'), 2800);
}

// Global active login mutation observer
const loginObserver = new MutationObserver(() => {
  const errorPopupLinks = document.querySelectorAll(
      '.switch-apps-gallery--error-popup-inner a[href*="/account"], .switch-apps-gallery--error-popup-inner a[href*="/login"]');
  errorPopupLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href && !href.includes('checkout_url=')) {
      const separator = href.includes('?') ? '&' : '?';
      link.setAttribute('href', `${href}${separator}checkout_url=${encodeURIComponent(window.location.href)}`);
    }
  });

  const commentsLinks = document.querySelectorAll(
      '.switch-apps-gallery-item-comments a[href*="/account"], .switch-apps-gallery-item-comments a[href*="/login"]');
  commentsLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href && !href.includes('checkout_url=')) {
      const separator = href.includes('?') ? '&' : '?';
      link.setAttribute('href', `${href}${separator}checkout_url=${encodeURIComponent(window.location.href)}`);
    }
  });
});
loginObserver.observe(document.body, {childList: true, subtree: true});

// Global close suggestions panel on clicking outside search areas
document.addEventListener('click', function(e) {
  const suggestionsPanel = document.querySelector('.custom-gallery-autocomplete-suggestions');
  const searchInput = document.querySelector('#custom-gallery-product-search-input');
  if (suggestionsPanel && searchInput) {
    if (!searchInput.contains(e.target) && !suggestionsPanel.contains(e.target)) {
      suggestionsPanel.style.display = 'none';
    }
  }
});

// Bulletproof Failsafe: Automatically unlock screen if scroll is locked but no lightbox modal is visible in viewport
function failsafeScrollUnlocker() {
  const isScrollLocked = document.body.style.overflow === 'hidden' ||
      document.documentElement.style.overflow === 'hidden' ||
      document.body.classList.contains('switch-apps-gallery-lock') ||
      document.body.classList.contains('uploadify-lock') ||
      document.documentElement.classList.contains('switch-apps-gallery-lock');

  if (isScrollLocked) {
    const visibleModals = Array.from(document.querySelectorAll(
        '.switch-apps-gallery--popup-inner, [class*="popup"], [class*="modal"], [class*="lightbox"]'
    )).filter(el => {
      if (el.classList.contains('custom-gallery-ui-container') || el.classList.contains('original-app-gallery-wrapper')) return false;
      return el.offsetWidth > 0 && el.offsetHeight > 0;
    });

    if (visibleModals.length === 0) {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      document.body.classList.remove('switch-apps-gallery-lock', 'uploadify-lock');
      document.documentElement.classList.remove('switch-apps-gallery-lock');
    }
  }
}
setInterval(failsafeScrollUnlocker, 1000);

// Global reference for title customizer MutationObserver to avoid duplication leaks
let activeTitleObserver = null;


// ── Section Specific Re-init integration logic (Theme customizer friendly) ──

function initPremiumGallery() {
  const customSection = document.querySelector('.custom-gallery-main-section');
  if (!customSection) return;

  const isCustomEnabled = customSection.getAttribute('data-custom-enabled') === 'true';
  if (!isCustomEnabled) {
    const grid = customSection.querySelector('.custom-gallery-grid-container');
    if (grid) grid.innerHTML = '';
    return;
  }

  const gridContainer = customSection.querySelector('.custom-gallery-grid-container');
  function showLoadingSpinner(message = 'Updating creations...') {
    document.body.classList.add('cg-is-loading');
    document.documentElement.classList.add('cg-is-loading');

    const originalWrapper = getOriginalWrapper();
    const originalLoader = originalWrapper ? (
      originalWrapper.querySelector('[class*="loader"]') || 
      originalWrapper.querySelector('[class*="loading"]')
    ) : null;

    gridContainer.innerHTML = `
      <div class="custom-gallery-grid-placeholder" role="status" aria-live="polite">
        <div class="custom-gallery-loader-container" style="display: flex; justify-content: center; align-items: center; margin-bottom: 15px;"></div>
        <p>${message}</p>
      </div>
    `;

    const loaderContainer = gridContainer.querySelector('.custom-gallery-loader-container');
    if (originalLoader && loaderContainer) {
      const clonedLoader = originalLoader.cloneNode(true);
      clonedLoader.style.removeProperty('opacity');
      clonedLoader.style.removeProperty('position');
      clonedLoader.style.removeProperty('pointer-events');
      clonedLoader.style.removeProperty('z-index');
      clonedLoader.style.removeProperty('width');
      clonedLoader.style.removeProperty('height');
      clonedLoader.style.removeProperty('overflow');
      clonedLoader.style.display = 'block';
      clonedLoader.style.visibility = 'visible';
      clonedLoader.style.opacity = '1';
      loaderContainer.appendChild(clonedLoader);
    } else {
      loaderContainer.innerHTML = `
        <div class="switch-apps-gallery-loader uploadify-loader switch-apps-loader switch-apps-gallery-grid-loader"></div>
      `;
    }
  }

  // 1. Hero Event Bindings
  const submitBtn = customSection.querySelector('.custom-gallery-submit-btn');
  const exploreBtn = customSection.querySelector('.custom-gallery-explore-btn');

  // Resolve the app's own upload CTA. This has to be the control itself: the app binds
  // its handler to the button, so clicking an ancestor never reaches it.
  function findOriginalUploadTrigger() {
    const scope = getOriginalWrapper() || document;
    const candidates = scope.querySelectorAll(
        '.switch-apps-gallery-hero__content button, .switch-apps-gallery-hero__content a.btn, ' +
        '.switch-apps-gallery-hero button, .switch-apps-gallery-hero a.btn');

    let firstHeroControl = null;
    for (const el of candidates) {
      // The app repeats its hero markup inside its own modals, where the first button is Close.
      if (el.closest('.switch-apps-gallery--popup, .switch-apps-gallery--error-popup')) continue;
      if (/upload|submit/i.test(el.textContent || '')) return el;
      if (!firstHeroControl) firstHeroControl = el;
    }
    return firstHeroControl;
  }

  if (submitBtn) {
    submitBtn.addEventListener('click', function() {
      let attempts = 0;
      (function openUploader() {
        const trigger = findOriginalUploadTrigger();
        if (trigger) {
          trigger.click();
          return;
        }
        // The app block can still be rendering on a slow connection.
        if (++attempts < 10) setTimeout(openUploader, 150);
      })();
    });
  }

  if (exploreBtn) {
    exploreBtn.addEventListener('click', function() {
      const searchBlock = customSection.querySelector('.custom-gallery-search-block');
      if (searchBlock) {
        searchBlock.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    });
  }

  // 2. Sort Dropdown Synced Event & Dynamic Option Copier
  const customSortSelect = customSection.querySelector('#custom-gallery-sort-select');
  
  function initSortDropdownSync() {
    const originalWrapper = getOriginalWrapper();
    if (!originalWrapper) {
      setTimeout(initSortDropdownSync, 300);
      return;
    }
    const originalSort = originalWrapper.querySelector('.switch-apps-gallery-results-sort select');
    if (!originalSort) {
      setTimeout(initSortDropdownSync, 300);
      return;
    }

    if (customSortSelect && originalSort.options.length > 0) {
      customSortSelect.innerHTML = '';
      Array.from(originalSort.options).forEach(opt => {
        const optionEl = document.createElement('option');
        optionEl.value = opt.value;
        optionEl.textContent = opt.textContent;
        optionEl.selected = opt.selected;
        customSortSelect.appendChild(optionEl);
      });
    }
  }

  if (customSortSelect) {
    customSortSelect.addEventListener('change', function() {
      const originalWrapper = getOriginalWrapper();
      if (!originalWrapper) return;
      const originalSort = originalWrapper.querySelector('.switch-apps-gallery-results-sort select');
      if (originalSort) {
        showLoadingSpinner('Sorting creations...');
        originalSort.value = customSortSelect.value;
        originalSort.dispatchEvent(new Event('change', {bubbles: true}));
      }
    });
  }

  initSortDropdownSync();

  // 3. Product Search with Autocomplete & Multi-Select Pills
  const searchInput = customSection.querySelector('#custom-gallery-product-search-input');
  const searchClear = customSection.querySelector('.custom-gallery-search-clear');
  const suggestionsPanel = customSection.querySelector('.custom-gallery-autocomplete-suggestions');
  const pillsContainer = customSection.querySelector('.custom-gallery-search-pills');

  let selectedProducts = []; // Stores objects: { title, handle }

  // Function to sync current pills from the URL filter.products on initial load
  function syncPillsFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const productHandles = urlParams.getAll('filter.products');
    
    if (productHandles.length > 0) {
      selectedProducts = productHandles.map(handle => {
        // Humanize handle for pill display if title isn't known yet
        const humanizedTitle = handle.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        return { title: humanizedTitle, handle: handle };
      });
      renderPills();
      if (searchClear) searchClear.style.display = 'flex';
    }
  }

  if (searchInput) {
    searchInput.addEventListener('input', function() {
      const query = searchInput.value.trim();

      if (query.length > 0) {
        if (searchClear) searchClear.style.display = 'flex';
        fetchSuggestions(query);
      } else {
        if (searchClear && selectedProducts.length === 0) {
          searchClear.style.display = 'none';
        }
        hideSuggestions();
      }
    });
  }

  if (searchClear) {
    searchClear.addEventListener('click', function() {
      if (searchInput.value.length > 0) {
        searchInput.value = '';
        searchClear.style.display = selectedProducts.length > 0 ? 'flex' : 'none';
        hideSuggestions();
      } else {
        showLoadingSpinner('Clearing product filters...');
        selectedProducts = [];
        searchClear.style.display = 'none';
        updateFilters();
      }
    });
  }

  function getOriginalProducts() {
    const originalWrapper = getOriginalWrapper();
    if (!originalWrapper) return [];

    // Find the specific products filter block inside the original gallery filters
    const productFilterBlock = Array.from(originalWrapper.querySelectorAll('.switch-apps-gallery-main-filter')).find(block => {
      const header = block.querySelector('.switch-apps-gallery-main-filter-header, .switch-apps-gallery-filter-header, h4, h5, span');
      return header && (header.textContent.toLowerCase().includes('product') || header.textContent.toLowerCase().includes('creation'));
    }) || originalWrapper.querySelector('.switch-apps-gallery-main-filter');

    if (!productFilterBlock) return [];

    const items = productFilterBlock.querySelectorAll('li, .switch-apps-gallery-filter-value, .switch-apps-gallery-main-filter-value, [data-filter-value]');
    const products = [];
    const seenTitles = new Set();

    items.forEach(el => {
      let clone = el.cloneNode(true);
      clone.querySelectorAll('input, checkbox, .count, .badge, .switch-apps-gallery-filter-count, .switch-apps-gallery-main-filter-count').forEach(child => child.remove());
      const text = clone.textContent.trim().replace(/\s*\(\d+\)\s*$/, '').replace(/\s*\[\d+\]\s*$/, '').trim();

      if (text && text.length > 1 && !seenTitles.has(text.toLowerCase())) {
        seenTitles.add(text.toLowerCase());
        
        let handle = '';
        const checkbox = el.querySelector('input[type="checkbox"]') || (el.tagName === 'INPUT' && el.type === 'checkbox' ? el : null);
        if (checkbox && checkbox.value) {
          handle = checkbox.value;
        } else {
          handle = text.toLowerCase()
                       .replace(/[^a-z0-9]+/g, '-')
                       .replace(/(^-|-$)+/g, '');
        }

        products.push({
          title: text,
          handle: handle,
          image: null,
          vendor: 'Altenew'
        });
      }
    });

    return products;
  }

  function fetchSuggestions(query) {
    const products = getOriginalProducts();
    const filtered = products.filter(p => p.title.toLowerCase().includes(query.toLowerCase()));
    renderSuggestions(filtered.slice(0, 10));
  }

  function renderSuggestions(products) {
    if (!suggestionsPanel) return;
    suggestionsPanel.innerHTML = '';

    if (products.length === 0) {
      suggestionsPanel.innerHTML = `<div class="custom-gallery-autocomplete-no-results" role="status">No products found</div>`;
      suggestionsPanel.style.display = 'block';
      return;
    }

    products.forEach(product => {
      const suggestionItem = document.createElement('button');
      suggestionItem.type = 'button';
      suggestionItem.className = 'custom-gallery-autocomplete-suggestion';

      const thumb = product.image ||
          'https://cdn.shopify.com/s/files/1/0388/7541/files/Crafter_s_Gallery-GC_Hero_Banner_3200x500_4b29d34c-2d1c-464e-9be3-6d92b5179b7b.jpg?v=1767193932';

      suggestionItem.innerHTML = `
        ${product.image ? `<img class="custom-gallery-autocomplete-suggestion-image" src="${product.image}" alt="${product.title}">` : ''}
        <div class="custom-gallery-autocomplete-suggestion-info">
          <span class="custom-gallery-autocomplete-suggestion-title">${product.title}</span>
          <span class="custom-gallery-autocomplete-suggestion-vendor">${product.vendor || 'Altenew'}</span>
        </div>
      `;

      suggestionItem.addEventListener('click', function() {
        showLoadingSpinner(`Filtering by ${product.title}...`);
        const alreadySelected = selectedProducts.some(p => p.handle === product.handle);
        if (!alreadySelected) {
          selectedProducts.push({ title: product.title, handle: product.handle });
        }
        searchInput.value = '';
        if (searchClear) searchClear.style.display = 'flex';
        hideSuggestions();
        updateFilters();
      });

      suggestionsPanel.appendChild(suggestionItem);
    });

    suggestionsPanel.style.display = 'block';
  }

  function hideSuggestions() {
    if (suggestionsPanel) suggestionsPanel.style.display = 'none';
  }

  // Sync pills and active tags filters
  function updateFilters() {
    try {
      const originalWrapper = getOriginalWrapper();
      if (!originalWrapper) return;

      renderPills();

      // Update the URL and reload so the app re-fetches. There is deliberately no
      // initial-load guard: on first render selectedProducts is rebuilt from the URL by
      // syncPillsFromURL, so the handles already match and nothing navigates. Guarding on a
      // flag that only a grid mutation could clear left an already-filtered page unable to
      // navigate at all, so removing the last filter spun forever on an empty grid.
      const url = new URL(window.location.href);
      const currentHandles = url.searchParams.getAll('filter.products').sort().join(',');
      const newHandles = selectedProducts.map(p => p.handle).sort().join(',');

      if (currentHandles !== newHandles) {
        url.searchParams.delete('filter.products');
        url.searchParams.delete('tags');

        selectedProducts.forEach(p => {
          url.searchParams.append('filter.products', p.handle);
        });

        if (window.Shopify && window.Shopify.designMode) {
          window.history.replaceState(null, null, url.toString());
        } else {
          window.location.href = url.toString();
          return;
        }
      }

      // Find the specific products filter block inside the original gallery filters
      const productFilterBlock = Array.from(originalWrapper.querySelectorAll('.switch-apps-gallery-main-filter')).find(block => {
        const header = block.querySelector('.switch-apps-gallery-main-filter-header, .switch-apps-gallery-filter-header, h4, h5, span');
        return header && (header.textContent.toLowerCase().includes('product') || header.textContent.toLowerCase().includes('creation'));
      }) || originalWrapper.querySelector('.switch-apps-gallery-main-filter');

      // Query only the list items, labels, and checkboxes inside this product filter block
      const elements = productFilterBlock ? productFilterBlock.querySelectorAll(
          'li, .switch-apps-gallery-filter-value, .switch-apps-gallery-main-filter-value, [data-filter-value], label, input[type="checkbox"]'
      ) : [];

      if (!elements || elements.length === 0) return;

      const checkedCheckboxes = originalWrapper.querySelectorAll('input[type="checkbox"]:checked');
      checkedCheckboxes.forEach(cb => {
        cb.checked = false;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      });

      selectedProducts.forEach(product => {
        const productTitle = product.title;
        const matchedEl = Array.from(elements).find(el => {
          return el && el.textContent && el.textContent.trim().toLowerCase().includes(productTitle.toLowerCase());
        });

        if (matchedEl) {
          const checkbox = matchedEl.querySelector('input[type="checkbox"]') || 
                           (matchedEl.tagName === 'INPUT' && matchedEl.type === 'checkbox' ? matchedEl : null) ||
                           matchedEl.closest('label')?.querySelector('input[type="checkbox"]') ||
                           matchedEl.closest('li')?.querySelector('input[type="checkbox"]');
          
          if (checkbox) {
            if (!checkbox.checked) {
              checkbox.checked = true;
              checkbox.dispatchEvent(new Event('change', { bubbles: true }));
              const triggerEl = matchedEl.tagName === 'INPUT' ? matchedEl.closest('label, li, span') || matchedEl : matchedEl;
              triggerEl.click();
            }
          } else {
            matchedEl.click();
          }
        }
      });

      if (selectedProducts.length === 0) {
        const originalClearBtn = originalWrapper.querySelector(
            '.switch-apps-gallery-main-filter-clear, button[class*="clear"], [data-filter-clear], a[class*="clear"]');
        if (originalClearBtn) {
          originalClearBtn.click();
        }
      }
    } catch (err) {
      console.error('Error in updateFilters:', err);
    }
  }

  syncPillsFromURL();

  function renderPills() {
    if (!pillsContainer) return;
    pillsContainer.innerHTML = '';

    selectedProducts.forEach(product => {
      const pill = document.createElement('div');
      pill.className = 'custom-gallery-pill';
      pill.innerHTML = `
        <span>${product.title}</span>
        <button type="button" class="custom-gallery-pill-remove" aria-label="Remove ${product.title} filter"><span aria-hidden="true">&times;</span></button>
      `;

      pill.querySelector('.custom-gallery-pill-remove').addEventListener('click', function() {
        showLoadingSpinner(`Removing filter ${product.title}...`);
        selectedProducts = selectedProducts.filter(p => p.handle !== product.handle);
        if (searchClear && selectedProducts.length === 0 && searchInput.value.length === 0) {
          searchClear.style.display = 'none';
        }
        updateFilters();
      });

      pillsContainer.appendChild(pill);
    });
  }

  // --- Infinite Scroll State ---
  let isLoadingMore = false;
  let lastOriginalCardCount = 0;
  let hasPreloadedOnce = false;

  function triggerOriginalLoadMore() {
    // Trigger global scroll events for native observers
    window.dispatchEvent(new Event('scroll'));
    document.dispatchEvent(new Event('scroll'));
    window.dispatchEvent(new Event('resize'));

    const container = document.querySelector('.switch-apps-gallery-main-grid-load-more') ||
                      document.querySelector('.uploadify-gallery-load-more');
    if (!container) return true; 

    const btn = container.querySelector('button') ||
                container.querySelector('a') ||
                container.querySelector('[role="button"]') ||
                container;

    if (!btn) return true;

    const vueInstance = btn.__vue__ || btn._vei || (btn.__vueParentComponent && btn.__vueParentComponent.proxy);
    if (vueInstance && typeof vueInstance.onClick === 'function') {
      vueInstance.onClick();
      return true;
    }

    btn.dispatchEvent(new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    }));
    return true;
  }

  // --- High-Performance Single-Item Sentinel Card Observer ---
  let sentinelCardObserver = null;

  // How far from the end of the loaded batch the sentinel sits, so the next page starts
  // loading shortly before the shopper reaches the bottom.
  const SENTINEL_OFFSET_FROM_END = 8;

  function updateSentinelCard(currentCount) {
    if (currentCount <= lastOriginalCardCount) return;
    lastOriginalCardCount = currentCount;

    const customCards = gridContainer.querySelectorAll('.custom-gallery-card');
    if (customCards.length === 0) return;

    // Anchor to the end of what is loaded rather than to the previous batch boundary. On the
    // very first render there is no previous batch, so the old index of 0 hit an early return
    // and the observer was never attached at all - which left the grid stuck on its first page
    // with no way to reach the rest of the gallery.
    const sentinelIndex = Math.max(0, customCards.length - SENTINEL_OFFSET_FROM_END);

    const newSentinelElement = customCards[sentinelIndex];
    if (!newSentinelElement) return;

    if (sentinelCardObserver) {
      sentinelCardObserver.disconnect();
    }

    sentinelCardObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !isLoadingMore) {
          const clicked = triggerOriginalLoadMore();
          if (clicked) {
            isLoadingMore = true;
            document.body.classList.add('cg-is-loading');
            document.documentElement.classList.add('cg-is-loading');
            
            sentinelCardObserver.disconnect();

            setTimeout(function() {
              if (isLoadingMore) {
                isLoadingMore = false;
                document.body.classList.remove('cg-is-loading');
                document.documentElement.classList.remove('cg-is-loading');
              }
            }, 8000);
          }
        }
      });
    }, {
      rootMargin: '600px 0px 600px 0px',
      threshold: 0
    });

    sentinelCardObserver.observe(newSentinelElement);
  }

  function hideOriginalLoadMoreButton(originalWrapper) {
    if (!originalWrapper) return;
    const btn = originalWrapper.querySelector('.switch-apps-gallery-main-grid-load-more button') ||
                originalWrapper.querySelector('.switch-apps-gallery-main-grid-load-more');
    if (btn) {
      btn.style.setProperty('opacity', '0', 'important');
      btn.style.setProperty('position', 'absolute', 'important');
      btn.style.setProperty('pointer-events', 'none', 'important');
      btn.style.setProperty('height', '0', 'important');
      btn.style.setProperty('width', '0', 'important');
      btn.style.setProperty('margin', '0', 'important');
      btn.style.setProperty('padding', '0', 'important');
      btn.style.setProperty('overflow', 'hidden', 'important');
    }
  }

  // ── High-Performance DOM-diffing gallery renderer ───────────────────
  function syncGalleryGrid(originalGrid) {
    const originalCards = originalGrid.querySelectorAll(
        '.switch-apps-gallery-item, .switch-apps-gallery-card, .grid-item, .uploadify-item');

    if (originalCards.length === 0) {
      const originalWrapper = getOriginalWrapper();
      const textContent = ((originalGrid ? originalGrid.textContent : '') + ' ' + (originalWrapper ? originalWrapper.textContent : '')).toLowerCase();
      
      const isNoPosts = textContent.includes('no posts') || 
                        textContent.includes('no media') || 
                        textContent.includes('no creations') || 
                        textContent.includes('no items') || 
                        textContent.includes('no results') || 
                        textContent.includes('no posts found') ||
                        (originalWrapper && (
                          originalWrapper.querySelector('.switch-apps-gallery-empty') !== null ||
                          originalWrapper.querySelector('.uploadify-empty') !== null ||
                          originalWrapper.querySelector('.no-posts') !== null ||
                          originalWrapper.querySelector('.empty-gallery') !== null
                        ));

      if (isNoPosts) {
        gridContainer.innerHTML = `
          <div class="custom-gallery-grid-placeholder no-posts-found" role="status" aria-live="polite" style="text-align: center; padding: 60px 20px;">
            <p style="font-size: 1.25rem; font-weight: 500; color: var(--cg-text-light, #777);">No Posts Found</p>
          </div>
        `;
      } else {
        // Try to clone the original loader dynamically from the original app wrapper
        const originalLoader = originalWrapper ? (
          originalWrapper.querySelector('[class*="loader"]') || 
          originalWrapper.querySelector('[class*="loading"]')
        ) : null;

        gridContainer.innerHTML = `
          <div class="custom-gallery-grid-placeholder">
            <div class="custom-gallery-loader-container" style="display: flex; justify-content: center; align-items: center; margin-bottom: 15px;"></div>
            <p>Rendering premium creations...</p>
          </div>
        `;

        const loaderContainer = gridContainer.querySelector('.custom-gallery-loader-container');
        if (originalLoader && loaderContainer) {
          const clonedLoader = originalLoader.cloneNode(true);
          clonedLoader.style.removeProperty('opacity');
          clonedLoader.style.removeProperty('position');
          clonedLoader.style.removeProperty('pointer-events');
          clonedLoader.style.removeProperty('z-index');
          clonedLoader.style.removeProperty('width');
          clonedLoader.style.removeProperty('height');
          clonedLoader.style.removeProperty('overflow');
          
          // Ensure it's visible in our layout context
          clonedLoader.style.display = 'block';
          clonedLoader.style.visibility = 'visible';
          clonedLoader.style.opacity = '1';
          
          loaderContainer.appendChild(clonedLoader);
        } else {
          loaderContainer.innerHTML = `
            <div class="switch-apps-gallery-loader uploadify-loader switch-apps-loader switch-apps-gallery-grid-loader"></div>
          `;
        }
      }
      return;
    }

    // Read Admin Theme settings dynamically from the DOM layout indicators
    const indicatorWrap = document.querySelector('.custom-gallery-layout-indicators');
    let itemsOrder = [];
    let enableSocial = false;
    let enableLikes = false;

    if (indicatorWrap) {
      const items = indicatorWrap.querySelectorAll('.card-layout-item');
      if (items.length > 0) {
        items.forEach(item => {
          const type = item.getAttribute('data-type');
          if (type) {
            itemsOrder.push(type);
            if (type === 'social') enableSocial = true;
            if (type === 'likes') enableLikes = true;
          }
        });
      }
    }

    if (itemsOrder.length === 0) {
      // Fallback if no indicators are found in DOM yet
      itemsOrder = ['image', 'title', 'username', 'likes', 'social'];
      enableSocial = true;
      enableLikes = true;
    }

    const enableUsername = itemsOrder.includes('username');

    const imgIndex = itemsOrder.indexOf('image') !== -1 ? itemsOrder.indexOf('image') : 0;
    const titleIndex = itemsOrder.indexOf('title') !== -1 ? itemsOrder.indexOf('title') : 1;
    const socialIndex = itemsOrder.indexOf('social') !== -1 ? itemsOrder.indexOf('social') : 3;

    // Resolve index of username / metadata row dynamically depending on block visibility
    const usernameIdxInOrder = itemsOrder.indexOf('username');
    const likesIdxInOrder = itemsOrder.indexOf('likes');
    let usernameIndex = 2;
    if (usernameIdxInOrder !== -1 && likesIdxInOrder !== -1) {
      usernameIndex = Math.min(usernameIdxInOrder, likesIdxInOrder);
    } else if (usernameIdxInOrder !== -1) {
      usernameIndex = usernameIdxInOrder;
    } else if (likesIdxInOrder !== -1) {
      usernameIndex = likesIdxInOrder;
    }

    const currentSettingsKey = `${enableSocial}-${enableLikes}-${itemsOrder.join(',')}`;
    const settingsChanged = gridContainer._lastSettings !== currentSettingsKey;
    gridContainer._lastSettings = currentSettingsKey;

    // Keep track of existing custom cards to reuse them efficiently (unless settings changed)
    const existingCards = new Map();
    if (!settingsChanged) {
      gridContainer.querySelectorAll('.custom-gallery-card').forEach(card => {
        if (card._cardData && card._cardData.imgSrc) {
          existingCards.set(card._cardData.imgSrc, card);
        }
      });
    } else {
      gridContainer.innerHTML = '';
    }

    const placeholder = gridContainer.querySelector('.custom-gallery-grid-placeholder');
    if (placeholder) {
      placeholder.remove();
    }

    const newCardElements = [];

    originalCards.forEach((originalCard, index) => {
      const imgEl = originalCard.querySelector('img');
      if (!imgEl) return;
      const imgSrc = imgEl.src;
      if (!imgSrc || imgSrc === window.location.href || imgSrc.startsWith('data:') || !imgSrc.startsWith('http')) return;

      const userEl = originalCard.querySelector('.switch-apps-gallery-item-username');
      const username = userEl ? userEl.textContent.trim() : 'Crafter';

      const titleEl = originalCard.querySelector('.switch-apps-gallery-item-title');
      const title = titleEl ? titleEl.textContent.trim() : '';

      const voteDiv = originalCard.querySelector('.switch-apps-gallery-item-votes');
      const voteSpan = voteDiv ? voteDiv.querySelector('span') : null;
      const likesCount = voteSpan ? voteSpan.textContent.replace(/[^\d]/g, '').trim() || '0' : '0';

      // Resolve database post ID
      let postId = '';
      const originalAnchor = originalCard.querySelector('a');
      if (originalAnchor) {
        const href = originalAnchor.getAttribute('href') || '';
        const match = href.match(/open-gallery-item=(\d+)/) || href.match(/gallery-item=(\d+)/);
        if (match && match[1]) {
          postId = match[1];
        }
      }

      if (!postId) {
        const idMatchEl = originalCard.querySelector('[id*="gallery-item"], [class*="gallery-item"]');
        if (idMatchEl) {
          const matchedAttr = idMatchEl.id || idMatchEl.className;
          const match = matchedAttr.match(/(\d+)/);
          if (match && match[1]) postId = match[1];
        }
      }

      if (!postId) {
        postId = imgSrc.split('/').pop().split('?')[0].replace('uploadify--', '').split('.')[0] || index.toString();
      }

      // Check if we can reuse an existing card DOM element
      let customCard = existingCards.get(imgSrc);
      if (customCard) {
        // Sync likes count if updated
        if (customCard._cardData) {
          customCard._cardData.likesCount = likesCount;
        }
        const likesSpan = customCard.querySelector('.custom-gallery-card-likes span');
        if (likesSpan) likesSpan.textContent = likesCount;
        
        newCardElements.push(customCard);
        existingCards.delete(imgSrc);
        return;
      }

      // Helper to generate Title element HTML
      function getTitleHTML() {
        return title ? `<div class="custom-gallery-card-title" style="order: ${titleIndex};">${title}</div>` : '';
      }

      // Helper to generate Meta Row HTML
      function getMetaRowHTML() {
        if (!enableUsername && !enableLikes) return '';
        return `
          <div class="custom-gallery-card-meta-row" style="order: ${usernameIndex}; display: flex; justify-content: space-between; align-items: center; width: 100%;">
            ${enableUsername ? `<div class="custom-gallery-card-user"><span>${username}</span></div>` : ''}
            ${enableLikes ? `
            <div class="custom-gallery-card-likes" style="display: flex; align-items: center; gap: 4px;">
              <svg viewBox="0 0 24 24" style="width: 14px; height: 14px; fill: currentColor;"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
              <span>${likesCount}</span>
            </div>
            ` : ''}
          </div>
        `;
      }

      // Helper to generate Social Row HTML
      function getSocialRowHTML() {
        if (!enableSocial) return '';
        return `
          <div class="cg-card-social-row" style="order: ${socialIndex}; display: flex; gap: 10px; margin-top: 8px; align-items: center; justify-content: flex-start;">
            <button type="button" class="cg-social-btn cg-share-facebook" title="Share on Facebook" aria-label="Share on Facebook" style="background: none; border: none; padding: 0; color: inherit; cursor: pointer; display: flex; align-items: center; opacity: 0.85; transition: opacity 0.15s ease;">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="width: 14px; height: 14px; fill: currentColor;"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg>
            </button>
            <button type="button" class="cg-social-btn cg-share-twitter" title="Share on Twitter / X" aria-label="Share on Twitter / X" style="background: none; border: none; padding: 0; color: inherit; cursor: pointer; display: flex; align-items: center; opacity: 0.85; transition: opacity 0.15s ease;">
              <svg fill="currentColor" viewBox="0 0 16 16" style="width: 13px; height: 13px;"><path d="M12.6 0.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867 -5.07 -4.425 5.07H0.316l5.733 -6.57L0 0.75h5.063l3.495 4.633L12.601 0.75Zm-0.86 13.028h1.36L4.323 2.145H2.865z" stroke-width="1"/></svg>
            </button>
            <button type="button" class="cg-social-btn cg-share-instagram" title="Share on Instagram" aria-label="Share on Instagram" style="background: none; border: none; padding: 0; color: inherit; cursor: pointer; display: flex; align-items: center; opacity: 0.85; transition: opacity 0.15s ease;">
              <svg viewBox="0 0 18 18" style="width: 14px; height: 14px; fill: currentColor;"><path fill="currentColor" d="M8.77 1.58c2.34 0 2.62.01 3.54.05.86.04 1.32.18 1.63.3.41.17.7.35 1.01.66.3.3.5.6.65 1 .12.32.27.78.3 1.64.05.92.06 1.2.06 3.54s-.01 2.62-.05 3.54a4.79 4.79 0 01-.3 1.63c-.17.41-.35.7-.66 1.01-.3.3-.6.5-1.01.66-.31.12-.77.26-1.63.3-.92.04-1.2.05-3.54.05s-2.62 0-3.55-.05a4.79 4.79 0 01-1.62-.3c-.42-.16-.7-.35-1.01-.66-.31-.3-.5-.6-.66-1a4.87 4.87 0 01-.3-1.64c-.04-.92-.05-1.2-.05-3.54s0-2.62.05-3.54c.04-.86.18-1.32.3-1.63.16-.41.35-.7.66-1.01.3-.3.6-.5 1-.65.32-.12.78-.27 1.63-.3.93-.05 1.2-.06 3.55-.06zm0-1.58C6.39 0 6.09.01 5.15.05c-.93.04-1.57.2-2.13.4-.57.23-1.06.54-1.55 1.02C1 1.96.7 2.45.46 3.02c-.22.56-.37 1.2-.4 2.13C0 6.1 0 6.4 0 8.77s.01 2.68.05 3.61c.04.94.2 1.57.4 2.13.23.58.54 1.07 1.02 1.56.49.48.98.78 1.55 1.01.56.22 1.2.37 2.13.4.94.05 1.24.06 3.62.06 2.39 0 2.68-.01 3.62-.05.93-.04 1.57-.2 2.13-.41a4.27 4.27 0 001.55-1.01c.49-.49.79-.98 1.01-1.56.22-.55.37-1.19.41-2.13.04-.93.05-1.23.05-3.61 0-2.39 0-2.68-.05-3.62a6.47 6.47 0 00-.4-2.13 4.27 4.27 0 00-1.02-1.55A4.35 4.35 0 0014.52.46a6.43 6.43 0 00-2.13-.41A69 69 0 008.77 0z"></path><path fill="currentColor" d="M8.8 4a4.5 4.5 0 100 9 4.5 4.5 0 000-9zm0 7.43a2.92 2.92 0 110-5.85 2.92 2.92 0 010 5.85zM13.43 5a1.05 1.05 0 100-2.1 1.05 1.05 0 000 2.1z"></path></svg>
            </button>
            <button type="button" class="cg-social-btn cg-share-copylink" title="Copy Direct Link" aria-label="Copy direct link" style="background: none; border: none; padding: 0; color: inherit; cursor: pointer; display: flex; align-items: center; opacity: 0.85; transition: opacity 0.15s ease;">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="width: 14px; height: 14px; fill: currentColor;"><path d="M13.723 18.654l-3.61 3.609c-2.316 2.315-6.063 2.315-8.378 0-1.12-1.118-1.735-2.606-1.735-4.188 0-1.582.615-3.07 1.734-4.189l4.866-4.865c2.355-2.355 6.114-2.262 8.377 0 .453.453.81.973 1.089 1.527l-1.593 1.592c-.18-.613-.5-1.189-.964-1.652-1.448-1.448-3.93-1.51-5.439-.001l-.001.002-4.867 4.865c-1.5 1.499-1.5 3.941 0 5.44 1.517 1.517 3.958 1.488 5.442 0l2.425-2.424c.993.284 1.791.335 2.654.284zm.161-16.918l-3.574 3.576c.847-.05 1.655 0 2.653.283l2.393-2.389c1.498-1.502 3.94-1.5 5.44-.001 1.517 1.518 1.486 3.959 0 5.442l-4.831 4.831-.003.002c-1.438 1.437-3.886 1.552-5.439-.002-.473-.474-.785-1.042-.956-1.643l-.084.068-1.517 1.515c.28.556.635 1.075 1.088 1.528 2.245 2.245 6.004 2.374 8.378 0l4.832-4.831c2.314-2.316 2.316-6.062-.001-8.377-2.317-2.321-6.067-2.313-8.379-.002z"/></svg>
            </button>
          </div>
        `;
      }

      const titleInTop = (titleIndex < imgIndex);
      const metaInTop = (usernameIndex < imgIndex);
      const socialInTop = (socialIndex < imgIndex);

      let topOverlayHTML = '';
      let bottomOverlayHTML = '';

      if (titleInTop) topOverlayHTML += getTitleHTML();
      else bottomOverlayHTML += getTitleHTML();

      if (metaInTop) topOverlayHTML += getMetaRowHTML();
      else bottomOverlayHTML += getMetaRowHTML();

      if (socialInTop) topOverlayHTML += getSocialRowHTML();
      else bottomOverlayHTML += getSocialRowHTML();

      // Create a new card element with flexible order styling
      customCard = document.createElement('div');
      customCard.className = 'custom-gallery-card';
      customCard.style.display = 'flex';
      customCard.style.flexDirection = 'column';
      customCard.style.gap = '3px';

      customCard.innerHTML = `
        ${topOverlayHTML.trim() ? `
        <div class="custom-gallery-card-overlay-top" style="display: flex; flex-direction: column; gap: 3px; padding: 8px 10px 0 10px;">
          ${topOverlayHTML}
        </div>
        ` : ''}

        <div class="custom-gallery-image-container" role="button" tabindex="0" aria-label="View ${title || 'creation'} by ${username}">
          <img class="custom-gallery-card-img" src="${imgSrc}" alt="${title || 'Creation'} by ${username}" loading="lazy" onload="this.classList.add('is-loaded')" onerror="this.classList.add('is-error')">
        </div>

        ${bottomOverlayHTML.trim() ? `
        <div class="custom-gallery-card-overlay" style="display: flex; flex-direction: column; gap: 3px; padding: 0 10px 8px 10px;">
          ${bottomOverlayHTML}
        </div>
        ` : ''}
      `;


      // Add loaded class listener to image for placeholder transition
      const cardImg = customCard.querySelector('.custom-gallery-card-img');
      if (cardImg) {
        if (cardImg.complete || cardImg.naturalWidth > 0) {
          cardImg.classList.add('is-loaded');
        } else {
          cardImg.addEventListener('load', function() {
            cardImg.classList.add('is-loaded');
          });
          cardImg.addEventListener('error', function() {
            cardImg.classList.add('is-error');
          });
        }
      }

      customCard._cardData = {
        postId,
        imgSrc,
        username,
        title,
        likesCount,
        originalCard
      };

      newCardElements.push(customCard);
    });

    // Remove obsolete cards
    existingCards.forEach(card => card.remove());

    // Append and sort new elements efficiently
    newCardElements.forEach(card => {
      if (card.parentNode !== gridContainer) {
        gridContainer.appendChild(card);
      }
    });

    const currentCount = originalCards.length;

    // Preload second page initially
    if (!hasPreloadedOnce && currentCount > 0) {
      hasPreloadedOnce = true;
      setTimeout(function() {
        triggerOriginalLoadMore();
      }, 1000);
    }

    if (isLoadingMore) {
      if (currentCount > lastOriginalCardCount) {
        isLoadingMore = false;
        document.body.classList.remove('cg-is-loading');
        document.documentElement.classList.remove('cg-is-loading');
        updateSentinelCard(currentCount);
      }
    } else {
      updateSentinelCard(currentCount);
    }
  }

  function initMutationObserver() {
    const originalWrapper = getOriginalWrapper();
    if (!originalWrapper) {
      setTimeout(initMutationObserver, 500);
      return;
    }

    const targetGridSelector = '.switch-apps-gallery-main-grid, .switch-apps-gallery-grid, [data-uploadify-grid], .switch-apps-customer-gallery .grid';
    const originalGrid = originalWrapper.querySelector(targetGridSelector);

    if (!originalGrid) {
      setTimeout(initMutationObserver, 500);
      return;
    }

    hideOriginalLoadMoreButton(originalWrapper);

    const config = {childList: true, subtree: true};

    const observer = new MutationObserver(function(mutationsList) {
      syncGalleryGrid(originalGrid);
      hideOriginalLoadMoreButton(originalWrapper);
      // Ensure selections are maintained if the original app resets filters during internal state updates
      updateFilters();
    });

    observer.observe(originalGrid, config);
    syncGalleryGrid(originalGrid);
  }

  // Consolidated Event Delegation for Custom Cards & Share Buttons
  gridContainer.addEventListener('click', function(e) {
    const cardEl = e.target.closest('.custom-gallery-card');
    if (!cardEl || !cardEl._cardData) return;

    const cardData = cardEl._cardData;

    // 1. Check if click targets a social sharing button
    const socialBtn = e.target.closest('.cg-social-btn');
    if (socialBtn) {
      e.stopPropagation();
      e.preventDefault();

      let platform = '';
      if (socialBtn.classList.contains('cg-share-facebook')) platform = 'facebook';
      else if (socialBtn.classList.contains('cg-share-twitter')) platform = 'twitter';
      else if (socialBtn.classList.contains('cg-share-instagram')) platform = 'instagram';
      else if (socialBtn.classList.contains('cg-share-copylink')) platform = 'copy';

      if (platform) {
        triggerNativeSocialClick(cardData, platform);
      }
      return;
    }

    // 2. Otherwise, click targets the card itself to trigger the premium lightbox
    const liveImage = findLiveOriginalImage(cardData.imgSrc);
    if (liveImage) {
      liveImage.click();
      return;
    }

    // Fallback image routing
    const originalWrapper = getOriginalWrapper();
    if (!originalWrapper) return;

    const activeAnchor = originalWrapper.querySelector(`a[href*="${cardData.postId}"]`) ||
                         originalWrapper.querySelector(`[id*="${cardData.postId}"] a`) ||
                         originalWrapper.querySelector(`[class*="${cardData.postId}"] a`);

    if (activeAnchor) {
      const imgInside = activeAnchor.querySelector('img') || activeAnchor;
      if (imgInside) imgInside.click();
    } else {
      if (cardData.originalCard && document.body.contains(cardData.originalCard)) {
        const fallbackTrigger = cardData.originalCard.querySelector('img') || cardData.originalCard;
        if (fallbackTrigger) fallbackTrigger.click();
      }
    }
  });

  // Keyboard activation for cards (the image acts as a button; Enter/Space opens the lightbox)
  gridContainer.addEventListener('keydown', function(e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    const trigger = e.target.closest('.custom-gallery-image-container[role="button"]');
    if (!trigger) return;
    e.preventDefault();
    trigger.click();
  });

  // ── Uploader Modal Title Customizer ──────────────────────────────
  if (activeTitleObserver) {
    activeTitleObserver.disconnect();
    activeTitleObserver = null;
  }

  const customSubmissionTitle = customSection.getAttribute('data-submission-title');
  if (customSubmissionTitle) {
    activeTitleObserver = new MutationObserver(() => {
      const submissionTitle = document.querySelector('.switch-apps-gallery-submission--title h3');
      if (submissionTitle && submissionTitle.textContent.trim() !== customSubmissionTitle.trim()) {
        submissionTitle.textContent = customSubmissionTitle;
      }
    });
    activeTitleObserver.observe(document.body, {childList: true, subtree: true});
  }

  initMutationObserver();
}

// ── Initialize gallery logic ────────────────────────────────────────

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPremiumGallery);
} else {
  initPremiumGallery();
}

// Support Shopify live theme customizer dynamic editor events
document.addEventListener('shopify:section:load', function(e) {
  if (e.target.querySelector('.custom-gallery-main-section') || e.target.classList.contains('custom-gallery-main-section')) {
    initPremiumGallery();
  }
});
